import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Check, X, ArrowRight, Globe, Phone, 
  Building, AlertTriangle, TrendingUp, FileCheck, Ban, MessageSquare,
  Target, Zap, Mail, Award, CreditCard, ArrowLeft, Star, 
  Users, ShieldCheck, Sparkles, DollarSign, Rocket, CheckCircle,
  Palette, Server, Lock, Send, Image, Briefcase
} from 'lucide-react';
import AfterPaymentBlock from '../components/catalog/AfterPaymentBlock';

// Problem items - without website
const problemItems = [
  { icon: Ban, text: 'Поставщик просит сайт — а его нет' },
  { icon: AlertTriangle, text: 'Даёте ссылку на Amazon — выглядит несолидно' },
  { icon: X, text: 'Форма заявки требует URL сайта' },
  { icon: AlertTriangle, text: 'Лендинг на Тильде "Мы торгуем на Амазон" — не вариант' },
  { icon: MessageSquare, text: 'Нет доменной почты — пишете с Gmail' },
  { icon: AlertTriangle, text: 'Не знаете, какой сайт нужен для Wholesale' },
];

// Solution items - with website
const solutionItems = [
  { icon: Check, text: 'Профессиональный сайт под ваш бренд' },
  { icon: Globe, text: 'Собственный домен (входит в стоимость)' },
  { icon: Mail, text: 'Доменная почта name@yourdomain.com' },
  { icon: Palette, text: 'Премиум дизайн — выглядит дорого' },
  { icon: Users, text: 'Фото команды, склада, референсы' },
  { icon: Send, text: 'Автоответ и рассылка после заявки' },
  { icon: ShieldCheck, text: 'Всё что нужно увидеть поставщику' },
];

// What supplier checks
const supplierChecks = [
  { 
    icon: Users, 
    title: 'Кто вы?', 
    desc: 'О компании, история, миссия',
    color: 'blue'
  },
  { 
    icon: Phone, 
    title: 'Контакты', 
    desc: 'Телефон, адрес, форма связи',
    color: 'emerald'
  },
  { 
    icon: Palette, 
    title: 'Дизайн', 
    desc: 'Хороший дизайн = ответственность',
    color: 'violet'
  },
  { 
    icon: Mail, 
    title: 'Бизнес-почта', 
    desc: 'name@company.com, не Gmail',
    color: 'amber'
  },
  { 
    icon: Image, 
    title: 'Визуал', 
    desc: 'Фото команды, склада, товаров',
    color: 'rose'
  },
  { 
    icon: Briefcase, 
    title: 'Референсы', 
    desc: 'С кем работаете, отзывы',
    color: 'cyan'
  },
];

// What's included
const includedItems = [
  {
    icon: Globe,
    title: 'Домен',
    desc: 'Регистрация домена на 1 год бесплатно',
    highlight: true,
  },
  {
    icon: Mail,
    title: 'Доменная почта',
    desc: 'Профессиональный email на вашем домене',
    highlight: true,
  },
  {
    icon: Palette,
    title: 'Премиум дизайн',
    desc: 'Современный, дорогой внешний вид',
  },
  {
    icon: Server,
    title: 'Хостинг',
    desc: 'Быстрый и надёжный хостинг',
  },
  {
    icon: Send,
    title: 'Автоответ',
    desc: 'Автоматическая рассылка после заявки',
  },
  {
    icon: Lock,
    title: 'SSL сертификат',
    desc: 'Безопасное соединение https://',
  },
  {
    icon: FileCheck,
    title: 'Контентное наполнение',
    desc: 'Тексты, фото, структура страниц',
  },
  {
    icon: Zap,
    title: 'Быстрая загрузка',
    desc: 'Оптимизация скорости сайта',
  },
];

const colorConfig = {
  blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', gradient: 'from-blue-500 to-cyan-500' },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', gradient: 'from-emerald-500 to-teal-500' },
  violet: { bg: 'bg-violet-500/10', border: 'border-violet-500/30', text: 'text-violet-400', gradient: 'from-violet-500 to-purple-500' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', gradient: 'from-amber-500 to-orange-500' },
  rose: { bg: 'bg-rose-500/10', border: 'border-rose-500/30', text: 'text-rose-400', gradient: 'from-rose-500 to-pink-500' },
  cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', text: 'text-cyan-400', gradient: 'from-cyan-500 to-blue-500' },
};

const WebsitePage = () => {
  return (
    <div className="min-h-screen bg-[#050506]">
      {/* Back Button - fixed on desktop, static on mobile */}
      <div className="hidden sm:block fixed top-4 left-4 z-50">
        <Link 
          to="/catalog"
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900/90 border border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-600 transition-all backdrop-blur-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Каталог</span>
        </Link>
      </div>

      {/* Hero Section */}
      <section className="relative pt-20 sm:pt-24 pb-16 overflow-hidden">
        {/* Mobile back button - in flow */}
        <div className="sm:hidden absolute top-4 left-4 z-20">
          <Link 
            to="/catalog"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/90 border border-zinc-700 text-zinc-300"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">Каталог</span>
          </Link>
        </div>
        
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[150px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-violet-500/10 rounded-full blur-[150px]" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          {/* Badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/20 to-violet-500/20 border border-blue-500/30">
              <Globe className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-semibold text-blue-300">Профессиональный сайт для Wholesale</span>
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              Разработка сайта
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-violet-400 to-blue-400">
                для работы с поставщиками
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-zinc-400 max-w-3xl mx-auto leading-relaxed">
              Профессиональный сайт, который откроет двери к лучшим поставщикам. 
              Премиум дизайн, доменная почта, всё что нужно для серьёзного бизнеса.
            </p>
          </div>

          {/* Price Card */}
          <div className="max-w-md mx-auto">
            <div className="relative p-6 rounded-2xl bg-gradient-to-b from-zinc-800/50 to-zinc-900/50 border-2 border-blue-500/30 backdrop-blur-sm">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <div className="px-4 py-1 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 text-white text-sm font-bold">
                  Всё включено
                </div>
              </div>
              
              <div className="text-center pt-4">
                <div className="flex items-baseline justify-center gap-2 mb-2">
                  <span className="text-3xl font-black text-white">Цена по запросу</span>
                </div>
                <p className="text-zinc-400 text-sm mb-6">Домен + почта + хостинг включены</p>
                
                <a
                  href="https://t.me/vadjik"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-blue-500 to-violet-500 text-white font-bold text-lg hover:from-blue-400 hover:to-violet-400 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-blue-500/25"
                >
                  <MessageSquare className="w-5 h-5" />
                  <span>Обсудить задачу</span>
                  <ArrowRight className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
              Зачем Амазонщику сайт?
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              При работе по модели Wholesale поставщики часто требуют сайт. 
              Давать ссылку на amazon.com — минимум несолидно.
            </p>
          </div>

          {/* Problem/Solution Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* Problems */}
            <div className="p-6 rounded-2xl bg-gradient-to-b from-red-500/5 to-zinc-900/50 border border-red-500/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <X className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-white">Без сайта</h3>
              </div>
              <div className="space-y-3">
                {problemItems.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-zinc-800/30">
                    <item.icon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="text-zinc-300 text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Solutions */}
            <div className="p-6 rounded-2xl bg-gradient-to-b from-emerald-500/5 to-zinc-900/50 border border-emerald-500/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <Check className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white">С нашим сайтом</h3>
              </div>
              <div className="space-y-3">
                {solutionItems.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-zinc-800/30">
                    <item.icon className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-zinc-300 text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Supplier Checks Section */}
      <section className="py-16 relative bg-zinc-900/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
              Что оценивает поставщик по сайту?
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              У многих поставщиков сайт — обязательное поле. Без него даже заявку не отправить.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {supplierChecks.map((item, i) => {
              const colors = colorConfig[item.color];
              return (
                <div 
                  key={i}
                  className={`p-5 rounded-2xl ${colors.bg} border ${colors.border} transition-all duration-300 hover:-translate-y-1`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors.gradient} flex items-center justify-center mb-4`}>
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                  <p className={`text-sm ${colors.text}`}>{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 mb-6">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-semibold text-emerald-300">Решение</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
              Мы делаем сайты, которые работают
            </h2>
            <p className="text-zinc-400 text-lg max-w-3xl mx-auto">
              Наши специалисты создают профессиональные сайты с премиум дизайном.
              Сайт будет выглядеть дорого, солидно, профессионально — именно то, что хотят видеть поставщики.
            </p>
          </div>

          {/* What's Included Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {includedItems.map((item, i) => (
              <div 
                key={i}
                className={`p-5 rounded-2xl border transition-all duration-300 hover:-translate-y-1 ${
                  item.highlight 
                    ? 'bg-gradient-to-b from-emerald-500/10 to-zinc-900/50 border-emerald-500/40' 
                    : 'bg-zinc-800/30 border-zinc-700/50'
                }`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                  item.highlight ? 'bg-emerald-500/20' : 'bg-zinc-700/50'
                }`}>
                  <item.icon className={`w-5 h-5 ${item.highlight ? 'text-emerald-400' : 'text-zinc-400'}`} />
                </div>
                <h3 className="text-base font-bold text-white mb-1">{item.title}</h3>
                <p className="text-sm text-zinc-400">{item.desc}</p>
                {item.highlight && (
                  <div className="mt-2 px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold inline-block">
                    Бесплатно
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Result Block */}
          <div className="p-8 rounded-2xl bg-gradient-to-r from-blue-500/10 via-violet-500/10 to-blue-500/10 border-2 border-blue-500/30">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center flex-shrink-0">
                <Rocket className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-black text-white mb-2">Результат</h3>
                <p className="text-zinc-300 leading-relaxed">
                  Вы получите профессиональный сайт, который производит впечатление серьёзного бизнеса.
                  Поставщики увидят солидную компанию с контактами, историей, референсами — 
                  всем, что нужно для принятия решения о сотрудничестве.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* After Payment Block */}
      <AfterPaymentBlock />

      {/* Final CTA */}
      <section className="py-16 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="relative p-8 sm:p-12 rounded-3xl overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-violet-500/20 to-blue-500/20" />
            <div className="absolute inset-0 border-2 border-blue-500/30 rounded-3xl" />
            
            <div className="relative text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center mx-auto mb-6">
                <Globe className="w-8 h-8 text-white" />
              </div>
              
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-4 px-2">
                Готовы к профессиональному сайту?
              </h2>
              <p className="text-zinc-300 text-base sm:text-lg mb-8 max-w-2xl mx-auto px-4">
                Домен, почта, хостинг — всё включено. 
                Производите впечатление серьёзного партнёра с первого контакта.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="https://t.me/vadjik"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-8 py-4 rounded-xl bg-gradient-to-r from-blue-500 to-violet-500 text-white font-bold text-lg hover:from-blue-400 hover:to-violet-400 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-blue-500/25"
                >
                  <MessageSquare className="w-5 h-5" />
                  <span>Обсудить задачу</span>
                  <ArrowRight className="w-5 h-5" />
                </a>
                
                <Link 
                  to="/catalog"
                  className="w-full sm:w-auto px-8 py-4 rounded-xl border-2 border-zinc-700 text-zinc-300 font-bold hover:border-zinc-600 hover:text-white transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span>Вернуться в каталог</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer spacer */}
      <div className="h-16" />
    </div>
  );
};

export default WebsitePage;
